import { useState, useEffect, useRef, useCallback } from "react";
import "./admin.scss";
import { auth, db } from "../../services/firebase";
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from "firebase/auth";
import { collection, getDocs, doc, setDoc, deleteDoc, query, writeBatch } from "firebase/firestore";
import Chart from '../../components/chart/Chart';
import Navbar from "../../components/navbar/Navbar";

import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import LinkIcon from '@mui/icons-material/Link';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import SearchIcon from '@mui/icons-material/Search';

const INITIAL_PROJECT = { id: "", link: "", description: "", thumbnail: "", header_image: "", hidden: true, media: [] };
const INITIAL_CODE = { urlId: "", oldUrlId: "", userName: "", id: "", allowed_projects: [] };

// Helper: insert Cloudinary transform for thumbnails
const cldThumb = (url, w = 150) => {
    if (url && url.includes('res.cloudinary.com') && url.includes('/upload/')) {
        return url.replace('/upload/', `/upload/w_${w},c_fill,f_auto,q_auto/`);
    }
    return url;
};

const Admin = () => {
    const [user, setUser] = useState(null);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [tab, setTab] = useState("projects");
    const [projects, setProjects] = useState([]);
    const [accessCodes, setAccessCodes] = useState([]);
    const [editingProject, setEditingProject] = useState(INITIAL_PROJECT);
    const [editingCode, setEditingCode] = useState(INITIAL_CODE);
    const [codeContextMenu, setCodeContextMenu] = useState({ show: false, x: 0, y: 0, code: null });
    const [draggedItemIndex, setDraggedItemIndex] = useState(null);
    const [draggedProjectIndex, setDraggedProjectIndex] = useState(null);
    const [showProjectPanel, setShowProjectPanel] = useState(false);
    const [showCodePanel, setShowCodePanel] = useState(false);
    const [projectSearch, setProjectSearch] = useState("");
    const [codeSearch, setCodeSearch] = useState("");
    const rightColProjectRef = useRef(null);
    const rightColCodeRef = useRef(null);
    const contextMenuRef = useRef(null);

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [modalTarget, setModalTarget] = useState('media'); // 'media' | 'thumbnail' | 'hidden_thumbnail'
    const [modalMode, setModalMode] = useState('paste'); // 'paste' | 'cloudinary' | 'video'
    const [pasteText, setPasteText] = useState('');
    const [cldFolder, setCldFolder] = useState('');
    const [cldImages, setCldImages] = useState([]);
    const [cldLoading, setCldLoading] = useState(false);
    const [videoUrl, setVideoUrl] = useState('');
    const [videoThumb, setVideoThumb] = useState('');
    const [showBlockModal, setShowBlockModal] = useState(false);
    const [blockTargetIndex, setBlockTargetIndex] = useState(null);

    // ─── Analytics State ────────────────────────────────────────
    const [globalAnalytics, setGlobalAnalytics] = useState(() => {
        try {
            const cached = localStorage.getItem('analytics_cache_data');
            return cached ? JSON.parse(cached) : null;
        } catch { return null; }
    });
    const [analyticsCacheDate, setAnalyticsCacheDate] = useState(() => {
        return localStorage.getItem('analytics_cache_date') || null;
    });
    const [analyticsLimit, setAnalyticsLimit] = useState(20);
    const [fetchUserName, setFetchUserName] = useState(() => {
        return localStorage.getItem('analytics_cache_user') || '';
    });
    const [indexError, setIndexError] = useState('');
    const [analyticsField, setAnalyticsField] = useState('login');

    const [chartList, setChartList] = useState([]);
    const [sortedList, setSortedList] = useState([]);


    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [rawLogs, setRawLogs] = useState([]);



    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (u) => {
            setUser(u);
            if (u) fetchData();
        });
        return () => unsub();
    }, []);

    const fetchData = async () => {
        const pSnap = await getDocs(collection(db, "projects"));
        const pArr = []; pSnap.forEach(d => pArr.push({ id: d.id, ...d.data() }));
        // Fallback sort just in case there are missing orders
        pArr.sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));
        setProjects(pArr);

        const cSnap = await getDocs(collection(db, "access_codes"));
        const cArr = []; cSnap.forEach(d => cArr.push({ urlId: d.id, ...d.data() }));
        setAccessCodes(cArr);
    };

    // ─── Project Drag & Drop ──────────────────────────────────────────
    const onProjectDragStart = (e, i) => { setDraggedProjectIndex(i); e.dataTransfer.effectAllowed = "move"; };
    const onProjectDragEnter = (e, i) => {
        e.preventDefault();
        // Disallow sorting if search filter is active
        if (projectSearch !== "") return;
        if (draggedProjectIndex === null || draggedProjectIndex === i) return;

        const p = [...projects];
        const dragged = p[draggedProjectIndex];
        p.splice(draggedProjectIndex, 1);
        p.splice(i, 0, dragged);
        setDraggedProjectIndex(i);
        setProjects(p);
    };
    const onProjectDragEnd = () => setDraggedProjectIndex(null);

    const saveProjectOrder = async () => {
        if (projectSearch !== "") return alert("Please clear search filter before saving order.");
        try {
            const batch = writeBatch(db);
            projects.forEach((p, index) => {
                const ref = doc(db, "projects", p.id);
                batch.set(ref, { order: index }, { merge: true });
            });
            await batch.commit();
            alert("Project order saved successfully!");
            fetchData();
        } catch (err) {
            alert("Error saving order: " + err.message);
        }
    };



    const handleLogin = async (e) => {
        e.preventDefault();
        try { await signInWithEmailAndPassword(auth, email, password); }
        catch (err) { alert("Login error: " + err.message); }
    };

    // ─── Analytics Functions ────────────────────────────────────
    const processAnalytics = useCallback((data, filter) => {
        if (!data || Object.keys(data).length === 0) {
            setSortedList([]); setChartList([]); return;
        }
        let keys = [];
        Object.keys(data).forEach(element => {
            if (!filter || JSON.stringify(data[element]).toLowerCase().includes(filter.toLowerCase())) {
                keys.push([Object.keys(data[element]).length, element]);
            }
        });
        keys.sort((a, b) => b[0] - a[0]);

        let sorted = [];
        const maxItems = 300;
        for (let i = 0; i < Math.min(keys.length, maxItems); i++) {
            const name = keys[i][1];
            const dict = data[keys[i][1]];
            let elementCounter = {};
            Object.keys(dict).forEach(view => {
                const day = new Date(dict[view].date).toDateString();
                elementCounter[day] = (elementCounter[day] || 0) + 1;
            });
            sorted.push({ [name]: elementCounter });
        }
        setSortedList(sorted);

        // Group by date for chart
        let chartDict = {};
        sorted.forEach(element => {
            Object.entries(element).forEach(([name, value]) => {
                Object.entries(value).forEach(([day, views]) => {
                    if (!chartDict[day]) chartDict[day] = {};
                    chartDict[day][name] = views;
                });
            });
        });

        let chartArr = [];
        Object.entries(chartDict).forEach(([date, data]) => {
            let dict = { date };
            Object.entries(data).forEach(([name, views]) => { dict[name] = views; });
            sorted.forEach(el => {
                Object.keys(el).forEach(name => { if (!(name in dict)) dict[name] = 0; });
            });
            chartArr.push(dict);
        });
        chartArr.sort((a, b) => new Date(a.date) - new Date(b.date));
        setChartList(chartArr);
    }, []);

    const triggerAnalyticsFetch = async () => {
        setAnalyticsLoading(true);
        setIndexError('');
        try {
            const { orderBy, limit, where } = require('firebase/firestore');
            let q;
            if (fetchUserName.trim()) {
                q = query(
                    collection(db, 'analytics'),
                    where('userName', '==', fetchUserName.trim()),
                    orderBy('timestamp', 'desc'),
                    limit(parseInt(analyticsLimit) || 20)
                );
            } else {
                q = query(collection(db, 'analytics'), orderBy('timestamp', 'desc'), limit(parseInt(analyticsLimit) || 20));
            }
            const snap = await getDocs(q);
            const docsArray = [];
            snap.forEach(d => {
                const data = d.data();
                // Strip timestamps which break JSON serialization
                if (data.timestamp) delete data.timestamp;
                docsArray.push({ id: d.id, ...data });
            });
            setGlobalAnalytics(docsArray);

            try {
                localStorage.setItem('analytics_cache_data', JSON.stringify(docsArray));
                const nowStr = new Date().toLocaleString();
                localStorage.setItem('analytics_cache_date', nowStr);
                localStorage.setItem('analytics_cache_user', fetchUserName.trim());
                setAnalyticsCacheDate(nowStr);
            } catch (e) {
                console.warn("Could not save to localStorage. Quota exceeded?", e);
            }

        } catch (err) {
            console.error('Error fetching analytics:', err);
            if (err.message && err.message.includes("requires an index")) {
                setIndexError("To search by a specific user, Firebase requires a Composite Index. Check the browser console (F12) for the direct link to auto-create it, or read here: " + err.message);
            } else {
                setIndexError("Error fetching data: " + err.message);
            }
        }
        setAnalyticsLoading(false);
    };

    const fetchAnalytics = useCallback((field) => {
        if (!globalAnalytics) return;
        setAnalyticsLoading(true);
        try {
            const nested = {};
            const rawDocs = [];

            // Filter locally by field
            globalAnalytics.forEach(d => {
                if (d.type === field) {
                    const cat = d.category || 'unknown';
                    if (!nested[cat]) nested[cat] = {};
                    nested[cat][d.id] = { date: d.date || Date.now(), search: d.userName || 'Anonymous' };
                    rawDocs.push(d);
                }
            });

            rawDocs.sort((a, b) => (b.date || 0) - (a.date || 0));
            setRawLogs(rawDocs);
            processAnalytics(nested, '');
        } catch (err) {
            console.error('Error filtering analytics:', err);
        }
        setAnalyticsLoading(false);
    }, [processAnalytics, globalAnalytics]);



    const resetAnalytics = async () => {
        if (!window.confirm("⚠️ WARNING: This will PERMANENTLY DELETE all analytics data (logins, visits, image views). Are you sure?")) return;
        if (!window.confirm("FINAL CONFIRMATION: Are you absolutely sure you want to clear all stats? This cannot be undone.")) return;

        setAnalyticsLoading(true);
        try {
            const q = query(collection(db, 'analytics'));
            const snap = await getDocs(q);

            // Firestore batch has a limit of 500 operations
            const chunks = [];
            const docs = snap.docs;
            for (let i = 0; i < docs.length; i += 500) {
                chunks.push(docs.slice(i, i + 500));
            }

            for (const chunk of chunks) {
                const batch = writeBatch(db);
                chunk.forEach(d => batch.delete(d.ref));
                await batch.commit();
            }

            alert("Analytics reset successfully.");
            setGlobalAnalytics(null); // Clear cache
            setAnalyticsCacheDate(null);
            setFetchUserName('');
            localStorage.removeItem('analytics_cache_data');
            localStorage.removeItem('analytics_cache_date');
            localStorage.removeItem('analytics_cache_user');
            // fetchAnalytics(analyticsField); // disabled
        } catch (err) {
            console.error("Error resetting analytics:", err);
            alert("Error resetting analytics: " + err.message);
        }
        setAnalyticsLoading(false);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    // ─── Auto-fetch dependencies ──────────────────────────────
    useEffect(() => {
        // Only run local memory filtering automatically when switching tabs
        if (tab === 'analytics' && user && globalAnalytics !== null) {
            fetchAnalytics(analyticsField);
        }
    }, [tab, analyticsField, user, globalAnalytics, fetchAnalytics]);



    // ─── Project Operations ─────────────────────────────────────
    const loadProject = async (p) => {
        let loadedMedia = p.media || [];
        if (p.id) {
            try {
                const { orderBy } = require("firebase/firestore");
                const mQuery = query(collection(db, "projects", p.id, "media"), orderBy("index"));
                const mSnap = await getDocs(mQuery);
                if (!mSnap.empty) {
                    loadedMedia = []; // Prefers subcollection if it exists
                    mSnap.forEach(d => loadedMedia.push({ id: d.id, ...d.data() }));
                }
            } catch (e) { console.error("Could not fetch media subcollection", e); }
        }
        setEditingProject({
            id: p.id, link: p.link || "", description: p.description || "",
            thumbnail: p.thumbnail || "", header_image: p.header_image || "", hidden_thumbnail: p.hidden_thumbnail || "", hidden_description: p.hidden_description || "",
            hidden: p.hidden !== undefined ? p.hidden : true, media: loadedMedia,
            order: p.order
        });
        setShowProjectPanel(true);
    };

    const handleProjectChange = (f, v) => {
        setEditingProject(prev => {
            let newState = { ...prev, [f]: v };
            return newState;
        });
    };

    const handleMediaField = (idx, field, value) => {
        const m = [...editingProject.media];
        m[idx] = { ...m[idx], [field]: value };
        handleProjectChange("media", m);
    };

    const deleteMedia = (idx) => handleProjectChange("media", editingProject.media.filter((_, i) => i !== idx));

    const saveProject = async (e) => {
        e.preventDefault();
        const docId = editingProject.id || editingProject.link.split('/').pop() || Date.now().toString();
        await setDoc(doc(db, "projects", docId), {
            link: editingProject.link,
            description: editingProject.description,
            thumbnail: editingProject.thumbnail,
            header_image: editingProject.header_image,
            hidden: editingProject.hidden,
            // Removed media array to save document size, using subcollections instead
            mediaCount: editingProject.media.length,
            order: editingProject.id ? (editingProject.order ?? projects.length) : projects.length
        });

        // Save subcollection using a batch
        const mBatch = writeBatch(db);
        const existingSnap = await getDocs(collection(db, "projects", docId, "media"));
        const existingIds = new Set(existingSnap.docs.map(d => d.id));

        editingProject.media.forEach((item, index) => {
            if (item.id) {
                // Update existing
                const ref = doc(db, "projects", docId, "media", item.id);
                mBatch.set(ref, { type: item.type, url: item.url, description: item.description || '', thumbnail: item.thumbnail || '', index: index, blocked_users: item.blocked_users || [] }, { merge: true });
                existingIds.delete(item.id);
            } else {
                // Add new
                const ref = doc(collection(db, "projects", docId, "media"));
                mBatch.set(ref, { type: item.type, url: item.url, description: item.description || '', thumbnail: item.thumbnail || '', index: index, blocked_users: item.blocked_users || [] });
            }
        });

        // Delete any leftovers
        existingIds.forEach(id => {
            mBatch.delete(doc(db, "projects", docId, "media", id));
        });

        await mBatch.commit();

        if (!editingProject.hidden) {
            const updates = accessCodes.map(async (code) => {
                if (!code.allowed_projects?.includes(editingProject.link)) {
                    await setDoc(doc(db, "access_codes", code.id), {
                        ...code,
                        allowed_projects: [...(code.allowed_projects || []), editingProject.link]
                    });
                }
            });
            await Promise.all(updates);
        }

        setEditingProject(INITIAL_PROJECT);
        setShowProjectPanel(false);
        fetchData();
    };

    const closeProjectPanel = () => {
        setShowProjectPanel(false);
        setEditingProject(INITIAL_PROJECT);
    };

    const syncMediaCounts = async () => {
        if (!window.confirm("This will scan all projects and update their media counts. Continue?")) return;
        setAnalyticsLoading(true); // Using existing loading state for simplicity or just alert
        try {
            const batch = writeBatch(db);
            for (const p of projects) {
                const mSnap = await getDocs(collection(db, "projects", p.id, "media"));
                const count = mSnap.size;
                batch.set(doc(db, "projects", p.id), { mediaCount: count }, { merge: true });
            }
            await batch.commit();
            alert("All project counts synced successfully!");
            fetchData();
        } catch (err) {
            alert("Error syncing counts: " + err.message);
        }
        setAnalyticsLoading(false);
    };

    const delProject = async (e, id) => {
        e.stopPropagation();
        if (window.confirm("Delete this project?")) {
            await deleteDoc(doc(db, "projects", id));
            if (editingProject.id === id) { setEditingProject(INITIAL_PROJECT); setShowProjectPanel(false); }
            fetchData();
        }
    };

    // ─── Drag & Drop ────────────────────────────────────────────
    const onDragStart = (e, i) => { setDraggedItemIndex(i); e.dataTransfer.effectAllowed = "move"; };
    const onDragEnter = (e, i) => {
        e.preventDefault();
        if (draggedItemIndex === null || draggedItemIndex === i) return;
        const m = [...editingProject.media];
        const dragged = m[draggedItemIndex];
        m.splice(draggedItemIndex, 1);
        m.splice(i, 0, dragged);
        setDraggedItemIndex(i);
        handleProjectChange("media", m);
    };
    const onDragEnd = () => setDraggedItemIndex(null);

    // ─── Add Media Modal ────────────────────────────────────────
    const openModal = (target = 'media') => { setModalTarget(target); setShowModal(true); setModalMode('paste'); setPasteText(''); setCldFolder(''); setCldImages([]); setVideoUrl(''); setVideoThumb(''); };
    const closeModal = () => { setShowModal(false); };

    const loadCloudinaryFolder = async () => {
        if (!cldFolder.trim()) return;
        setCldLoading(true);
        try {
            const res = await fetch('/api/listCloudinaryFolder', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ folder: cldFolder.trim() })
            });
            const data = await res.json();
            if (data.images) {
                setCldImages(data.images.map(img => ({ ...img, selected: modalTarget === 'media' })));
            } else {
                alert("No images found or error: " + (data.error || "unknown"));
            }
        } catch (err) {
            alert("Error loading folder: " + err.message);
        }
        setCldLoading(false);
    };

    const toggleCldImage = (idx) => {
        if (cldImages[idx].broken) return; // Can't select broken images
        const updated = [...cldImages];
        if (modalTarget !== 'media') {
            updated.forEach(img => img.selected = false);
            updated[idx].selected = true;
        } else {
            updated[idx].selected = !updated[idx].selected;
        }
        setCldImages(updated);
    };

    const markBroken = (idx) => {
        const updated = [...cldImages];
        updated[idx].broken = true;
        updated[idx].selected = false;
        setCldImages(updated);
    };

    const selectAllCld = (val) => setCldImages(cldImages.map(img => img.broken ? img : { ...img, selected: val }));

    const addPastedUrls = () => {
        const urls = pasteText.split('\n').map(u => u.trim()).filter(u => u.startsWith('http'));
        if (urls.length === 0) return alert("No valid URLs found");
        if (modalTarget === 'media') {
            const newItems = urls.map(url => ({ type: 'image', url, description: '' }));
            handleProjectChange('media', [...editingProject.media, ...newItems]);
        } else {
            handleProjectChange(modalTarget, urls[0]);
        }
        closeModal();
    };

    const addCloudinarySelection = () => {
        const selected = cldImages.filter(i => i.selected);
        if (selected.length === 0) return alert("No images selected");
        if (modalTarget === 'media') {
            const newItems = selected.map(img => ({ type: 'image', url: img.url, description: '' }));
            handleProjectChange('media', [...editingProject.media, ...newItems]);
        } else {
            handleProjectChange(modalTarget, selected[0].url);
        }
        closeModal();
    };

    const addVideo = () => {
        if (!videoUrl.trim()) return alert("Video URL is required");
        let cleanUrl = videoUrl.trim().replace(/["']/g, '');
        let cleanThumb = videoThumb.trim().replace(/["']/g, '');
        const newItem = { type: 'video', url: cleanUrl, thumbnail: cleanThumb, description: '' };
        handleProjectChange('media', [...editingProject.media, newItem]);
        closeModal();
    };

    const openBlockModal = (idx) => { setBlockTargetIndex(idx); setShowBlockModal(true); };
    const closeBlockModal = () => { setShowBlockModal(false); setBlockTargetIndex(null); };

    const toggleBlockedUser = (urlId) => {
        if (blockTargetIndex === null) return;
        const m = [...editingProject.media];
        const currentBlocks = m[blockTargetIndex].blocked_users || [];
        if (currentBlocks.includes(urlId)) {
            m[blockTargetIndex].blocked_users = currentBlocks.filter(id => id !== urlId);
        } else {
            m[blockTargetIndex].blocked_users = [...currentBlocks, urlId];
        }
        handleProjectChange("media", m);
    };



    const loadCode = (c) => {
        const codeData = { urlId: c.urlId, oldUrlId: c.urlId, userName: c.userName || "", id: c.id || "", allowed_projects: c.allowed_projects || [] };
        setEditingCode(codeData);
        setShowCodePanel(true);
    };

    const duplicateCode = (c) => {
        setEditingCode({ urlId: "", oldUrlId: "", userName: c.userName || "", id: (c.id || "") + " (Copy)", allowed_projects: [...(c.allowed_projects || [])] });
        setShowCodePanel(true);
    };

    const toggleRoute = (route) => {
        const cur = [...editingCode.allowed_projects];
        setEditingCode({ ...editingCode, allowed_projects: cur.includes(route) ? cur.filter(r => r !== route) : [...cur, route] });
    };

    const saveCode = async (e) => {
        e.preventDefault();
        if (!editingCode.urlId) return alert("Password (Link ID) is required");

        if (editingCode.oldUrlId && editingCode.oldUrlId !== editingCode.urlId) {
            await deleteDoc(doc(db, "access_codes", editingCode.oldUrlId));
        }

        await setDoc(doc(db, "access_codes", editingCode.urlId), {
            userName: editingCode.userName,
            id: editingCode.id,
            allowed_projects: editingCode.allowed_projects
        });
        setEditingCode(INITIAL_CODE);
        setShowCodePanel(false);
        fetchData();
    };

    const delCode = async (e, urlId) => {
        if (e) e.stopPropagation();
        const code = accessCodes.find(x => x.urlId === urlId);
        if (code?.allowed_projects?.includes('/dashboard')) {
            alert("This access code represents an administrator and cannot be deleted.");
            return;
        }
        if (window.confirm("Delete this access code?")) {
            await deleteDoc(doc(db, "access_codes", urlId));
            if (editingCode.urlId === urlId) { setEditingCode(INITIAL_CODE); setShowCodePanel(false); }
            fetchData();
        }
    };

    const closeCodePanel = () => {
        setShowCodePanel(false);
        setEditingCode(INITIAL_CODE);
    };

    const handleCodeContextMenu = (e, c) => {
        e.preventDefault();
        setCodeContextMenu({
            show: true,
            x: e.pageX,
            y: e.pageY,
            code: c
        });
    };

    const closeContextMenu = useCallback(() => {
        setCodeContextMenu({ ...codeContextMenu, show: false });
    }, [codeContextMenu]);

    // Close context menu on click outside
    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (codeContextMenu.show && contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
                closeContextMenu();
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, [codeContextMenu.show, closeContextMenu]);

    // ─── Click outside to close right panel ─────────────────────
    useEffect(() => {
        const handleClickOutside = (e) => {
            // Don't close if a modal is open
            if (showModal || showBlockModal) return;

            if (showProjectPanel && rightColProjectRef.current && !rightColProjectRef.current.contains(e.target)) {
                // Check if click was on a left panel item (those open the panel, not close it)
                const leftCol = rightColProjectRef.current?.parentElement?.querySelector('.leftCol');
                if (leftCol && leftCol.contains(e.target)) return;
                closeProjectPanel();
            }
            if (showCodePanel && rightColCodeRef.current && !rightColCodeRef.current.contains(e.target)) {
                const leftCol = rightColCodeRef.current?.parentElement?.querySelector('.leftCol');
                if (leftCol && leftCol.contains(e.target)) return;
                closeCodePanel();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showProjectPanel, showCodePanel, showModal, showBlockModal]);

    // ─── Render ─────────────────────────────────────────────────
    if (!user) {
        return (
            <div className="adminPage">
                <h1>Admin Login</h1>
                <form className="loginForm" onSubmit={handleLogin}>
                    <input type="email" placeholder="Admin Email" onChange={e => setEmail(e.target.value)} required />
                    <input type="password" placeholder="Admin Password" autoComplete="current-password" onChange={e => setPassword(e.target.value)} required />
                    <button type="submit">Access Control Panel</button>
                </form>
            </div>
        );
    }

    const displayRoutes = projects.map(p => p.link);

    const formatLogDate = (timestamp) => {
        if (!timestamp) return 'Unknown';
        const d = new Date(timestamp);
        return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' }) + ' ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    };

    const typeLabels = { login: '🔑 Login', visit: '👁️ Visit', image_view: '🖼️ Image' };

    const renderLogEntry = (log, idx) => (
        <div key={log.id || idx} className="logEntry">
            <span className="logTime">{formatLogDate(log.date)}</span>
            <span className={`logType ${log.type}`}>{typeLabels[log.type] || log.type}</span>
            <span className="logUser">{log.userName || 'Anonymous'}</span>
            <span className="logCategory">{log.category || '—'}</span>
            {log.url && <span className="logUrl" title={log.url}>{log.url}</span>}
        </div>
    );

    return (
        <div className="adminPage">
            <Navbar variant="dark" />

            <div className="adminDashboard">
                <div className="dashboardHeader">
                    <div className="tabs">
                        <button className={tab === "projects" ? "active" : ""} onClick={() => { setTab("projects"); setShowCodePanel(false); setEditingCode(INITIAL_CODE); }}>Project Manager</button>
                        <button className={tab === "codes" ? "active" : ""} onClick={() => { setTab("codes"); setShowProjectPanel(false); setEditingProject(INITIAL_PROJECT); }}>Access Codes</button>
                        <button className={tab === "analytics" ? "active" : ""} onClick={() => { setTab("analytics"); setShowProjectPanel(false); setShowCodePanel(false); }}>Analytics</button>
                    </div>
                    <button className="signOutBtn" onClick={() => signOut(auth)}>Sign Out</button>
                </div>

                {tab === "projects" && (
                    <div className="tabContent">
                        <div className="leftCol">
                            <h2>Your Projects
                                <div>
                                    <button className="newBtn" onClick={saveProjectOrder} style={{ marginRight: '8px' }}>Save Order</button>
                                    <button className="newBtn" onClick={syncMediaCounts} style={{ marginRight: '8px' }}>Sync Counts</button>
                                    <button className="newBtn" onClick={() => { setEditingProject(INITIAL_PROJECT); setShowProjectPanel(true); }}><AddCircleOutlineIcon style={{ fontSize: '1rem', verticalAlign: 'middle', marginRight: 4 }} /> New</button>
                                </div>
                            </h2>
                            <div className="searchBar">
                                <SearchIcon style={{ color: '#8892b0', fontSize: '1.2rem' }} />
                                <input type="text" placeholder="Search projects by URL..." value={projectSearch} onChange={e => setProjectSearch(e.target.value)} />
                            </div>
                            <div className="itemList">
                                {projects.filter(p => !projectSearch || p.link.toLowerCase().includes(projectSearch.toLowerCase())).map((p) => {
                                    const actualIndex = projects.findIndex(x => x.id === p.id);
                                    return (
                                        <div key={p.id} className={`itemCard ${editingProject.id === p.id ? 'selected' : ''}`} onClick={() => loadProject(p)}
                                            draggable={projectSearch === ""}
                                            onDragStart={e => onProjectDragStart(e, actualIndex)}
                                            onDragEnter={e => onProjectDragEnter(e, actualIndex)}
                                            onDragEnd={onProjectDragEnd} onDragOver={e => e.preventDefault()}
                                        >
                                            <div className="dragHandle" style={{ marginRight: '10px', color: '#8892b0', cursor: projectSearch === "" ? 'grab' : 'default', display: 'flex', alignItems: 'center' }}><DragIndicatorIcon style={{ fontSize: '1.2rem' }} /></div>
                                            <div className="info" style={{ flex: 1 }}>
                                                <span className="title">{p.link}</span>
                                                <span className="subtitle">{(p.mediaCount !== undefined ? p.mediaCount : (p.media || []).length)} media items</span>
                                                <span className={`tag ${p.hidden ? 'private' : 'public'}`}>
                                                    {p.hidden ? <LockOutlinedIcon style={{ fontSize: '0.75rem', verticalAlign: 'bottom' }} /> : <PublicOutlinedIcon style={{ fontSize: '0.75rem', verticalAlign: 'bottom' }} />}
                                                    {p.hidden ? ' Private' : ' Public'}
                                                </span>
                                            </div>
                                            <button className="delBtn" onClick={(e) => delProject(e, p.id)}><DeleteOutlineIcon /></button>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>

                        {showProjectPanel && <div className="rightCol" ref={rightColProjectRef}>
                            <h2>{editingProject.id ? 'Edit Project' : 'Create New Project'} {editingProject.id && <EditOutlinedIcon style={{ opacity: 0.5 }} />}</h2>
                            <form className="itemForm" onSubmit={saveProject}>
                                <div className="inputGroup">
                                    <label>Document ID</label>
                                    <input type="text" value={editingProject.id} onChange={e => handleProjectChange("id", e.target.value)} disabled={!!editingProject.id} />
                                </div>
                                <div className="inputGroup">
                                    <label>URL / Link Path</label>
                                    <input required type="text" placeholder="/work/halloweenia" value={editingProject.link} onChange={e => handleProjectChange("link", e.target.value)} />
                                </div>
                                <div className="inputGroup">
                                    <label>Description</label>
                                    <textarea placeholder="Short description..." value={editingProject.description} onChange={e => handleProjectChange("description", e.target.value)} />
                                </div>
                                <div className="inputGroup">
                                    <label>Public Thumbnail URL</label>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        {editingProject.thumbnail ? <img src={cldThumb(editingProject.thumbnail, 60)} alt="" style={{ width: 44, height: 44, borderRadius: 6, objectFit: 'cover', border: '1px solid rgba(255,255,255,0.1)' }} /> : <div style={{ width: 44, height: 44, borderRadius: 6, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)' }} />}
                                        <input type="text" placeholder="https://..." value={editingProject.thumbnail} onChange={e => handleProjectChange("thumbnail", e.target.value)} style={{ flex: 1 }} />
                                        <button type="button" onClick={() => openModal('thumbnail')} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', padding: '0 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}>Select Image</button>
                                    </div>
                                </div>
                                <div className="inputGroup">
                                    <label>Header Image URL (Logo)</label>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        {editingProject.header_image ? <img src={cldThumb(editingProject.header_image, 100)} alt="" style={{ height: 44, borderRadius: 6, objectFit: 'contain', border: '1px solid rgba(255,255,255,0.1)' }} /> : <div style={{ width: 44, height: 44, borderRadius: 6, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)' }} />}
                                        <input type="text" placeholder="https://..." value={editingProject.header_image || ""} onChange={e => handleProjectChange("header_image", e.target.value)} style={{ flex: 1 }} />
                                        <button type="button" onClick={() => openModal('header_image')} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', padding: '0 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}>Select</button>
                                    </div>
                                </div>
                                <div className="switchGroup" onClick={() => handleProjectChange("hidden", !editingProject.hidden)}>
                                    <label><input type="checkbox" checked={editingProject.hidden} readOnly /> Keep hidden? (Requires Access Code)</label>
                                </div>



                                {/* ─── Media Gallery ─── */}
                                <div className="mediaFlowEditor">
                                    <div className="mediaHeader">
                                        <h3>Gallery Media ({editingProject.media.length})</h3>
                                        <button type="button" className="addMediaBtn" onClick={() => openModal('media')}>
                                            <AddCircleOutlineIcon style={{ fontSize: '1rem', verticalAlign: 'middle', marginRight: 4 }} /> Add Media
                                        </button>
                                    </div>

                                    {editingProject.media.length === 0 && (
                                        <div className="emptyMedia">
                                            <ImageIcon style={{ fontSize: '2rem', opacity: 0.3 }} />
                                            <p>No media yet. Click "Add Media" to start.</p>
                                        </div>
                                    )}

                                    {editingProject.media.map((m, idx) => (
                                        <div key={idx} className="mediaRow"
                                            draggable onDragStart={e => onDragStart(e, idx)}
                                            onDragEnter={e => onDragEnter(e, idx)}
                                            onDragEnd={onDragEnd} onDragOver={e => e.preventDefault()}>
                                            <div className="dragHandle"><DragIndicatorIcon /></div>
                                            <div className="thumbPreview">
                                                {m.type === 'video' ? (
                                                    m.thumbnail ? <img src={cldThumb(m.thumbnail)} alt="" /> : <VideocamIcon style={{ color: '#8892b0' }} />
                                                ) : (
                                                    m.url ? <img src={cldThumb(m.url)} alt="" /> : <ImageIcon style={{ color: '#8892b0' }} />
                                                )}
                                            </div>
                                            <div className="mediaContent">
                                                <div className="urlLine">
                                                    <span className={`typeBadge ${m.type}`}>{m.type === 'video' ? '🎬' : '🖼️'}</span>
                                                    <span className="urlText" title={m.url}>{m.url ? (m.url.length > 60 ? '...' + m.url.slice(-57) : m.url) : 'No URL'}</span>
                                                </div>
                                                <input className="descInput" type="text" placeholder="Description (shown in fullscreen)..."
                                                    value={m.description || ""} onChange={e => handleMediaField(idx, "description", e.target.value)} />
                                                {m.type === 'video' && (
                                                    <input className="descInput" type="text" placeholder="Thumbnail image URL..."
                                                        value={m.thumbnail || ""} onChange={e => handleMediaField(idx, "thumbnail", e.target.value)} />
                                                )}
                                                <button type="button"
                                                    onClick={() => openBlockModal(idx)}
                                                    style={{ marginTop: '8px', padding: '6px 10px', background: (m.blocked_users && m.blocked_users.length > 0) ? 'rgba(230, 34, 67, 0.2)' : 'rgba(255,255,255,0.05)', border: `1px solid ${(m.blocked_users && m.blocked_users.length > 0) ? '#e62243' : 'rgba(255,255,255,0.1)'}`, color: (m.blocked_users && m.blocked_users.length > 0) ? '#e62243' : 'white', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px', width: 'fit-content' }}>
                                                    <LockOutlinedIcon style={{ fontSize: '1rem' }} />
                                                    {(m.blocked_users && m.blocked_users.length > 0) ? `Locked from ${m.blocked_users.length} user(s)` : 'Allow All Users'}
                                                </button>
                                            </div>
                                            <button type="button" className="delBtn" onClick={() => deleteMedia(idx)}><DeleteOutlineIcon style={{ fontSize: '1rem' }} /></button>
                                        </div>
                                    ))}
                                </div>

                                <div className="actions">
                                    <button type="button" className="cancel" onClick={closeProjectPanel}>Reset</button>
                                    <button type="submit" className="save">Save Project</button>
                                </div>
                            </form>
                        </div>}
                    </div>
                )}

                {tab === "codes" && (
                    <div className="tabContent">
                        <div className="leftCol">
                            <h2>Access Codes <button className="newBtn" onClick={() => { setEditingCode(INITIAL_CODE); setShowCodePanel(true); }}><AddCircleOutlineIcon style={{ fontSize: '1rem', verticalAlign: 'middle', marginRight: 4 }} /> New</button></h2>
                            <div className="searchBar">
                                <SearchIcon style={{ color: '#8892b0', fontSize: '1.2rem' }} />
                                <input type="text" placeholder="Search by code or user name..." value={codeSearch} onChange={e => setCodeSearch(e.target.value)} />
                            </div>
                            <div className="itemList">
                                {accessCodes.filter(c => !codeSearch || c.urlId.toLowerCase().includes(codeSearch.toLowerCase()) || (c.userName && c.userName.toLowerCase().includes(codeSearch.toLowerCase())) || (c.id && c.id.toLowerCase().includes(codeSearch.toLowerCase()))).map(c => {
                                    const isAdminCode = c.allowed_projects?.includes('/dashboard');
                                    return (
                                        <div key={c.urlId} className={`itemCard ${editingCode.urlId === c.urlId ? 'selected' : ''}`}
                                            onClick={() => loadCode(c)}
                                            onContextMenu={(e) => handleCodeContextMenu(e, c)}
                                        >
                                            <div className="info">
                                                <span className="title">
                                                    {c.id || "No Name"}
                                                    {isAdminCode && <span style={{ marginLeft: 8, padding: '2px 6px', background: 'rgba(230,34,67,0.2)', border: '1px solid rgba(230,34,67,0.5)', color: '#e62243', fontSize: '10px', borderRadius: '4px' }}>ADMIN</span>}
                                                </span>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '4px' }}>
                                                    <span className="subtitle" style={{ fontSize: '0.75rem', opacity: 0.8 }}>Name: {c.userName || "None"}</span>
                                                    <span className="subtitle" style={{ fontSize: '0.7rem', opacity: 0.6 }}>Pass: {c.urlId}</span>
                                                </div>
                                            </div>
                                            {isAdminCode ? (
                                                <div style={{ padding: '0 8px', color: '#e62243', opacity: 0.5 }}>🛡️</div>
                                            ) : (
                                                <button className="delBtn" onClick={(e) => delCode(e, c.urlId)}><DeleteOutlineIcon /></button>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                        {showCodePanel && <div className="rightCol" ref={rightColCodeRef}>
                            <h2>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {editingCode.urlId ? 'Edit Access Code' : 'Create Access Code'}
                                    {editingCode.urlId && <EditOutlinedIcon style={{ opacity: 0.5 }} />}
                                </span>
                                {editingCode.urlId && (
                                    <button
                                        type="button"
                                        className="newBtn"
                                        style={{ background: 'rgba(230, 34, 67, 0.2)', borderColor: 'rgba(230, 34, 67, 0.4)', color: 'white' }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            // Back up current state (admin)
                                            const curUser = localStorage.getItem('user');
                                            const curUserId = localStorage.getItem('userId');
                                            const curPass = localStorage.getItem('password');
                                            const curProjs = localStorage.getItem('allowed_projects');

                                            if (curUser) localStorage.setItem('original_user', curUser);
                                            if (curUserId) localStorage.setItem('original_userId', curUserId);
                                            if (curPass) localStorage.setItem('original_password', curPass);
                                            if (curProjs) localStorage.setItem('original_allowed_projects', curProjs);

                                            // Set impersonate state
                                            localStorage.setItem('impersonating', 'true');
                                            localStorage.setItem('user', editingCode.userName || editingCode.urlId);
                                            localStorage.setItem('userId', editingCode.id);
                                            localStorage.setItem('password', editingCode.urlId);
                                            localStorage.setItem('allowed_projects', JSON.stringify(editingCode.allowed_projects || []));

                                            window.location.href = '/home';
                                        }}
                                    >
                                        Impersonate
                                    </button>
                                )}
                            </h2>
                            <form className="itemForm" onSubmit={saveCode}>
                                <div className="inputGroup">
                                    <label>Display Name</label>
                                    <input required type="text" placeholder="e.g. Disney Animation" value={editingCode.userName}
                                        onChange={e => setEditingCode({ ...editingCode, userName: e.target.value })} />
                                </div>
                                <div className="inputGroup">
                                    <label>Internal ID (For administration only)</label>
                                    <input required type="text" placeholder="e.g. disney_anim_1" value={editingCode.id}
                                        onChange={e => setEditingCode({ ...editingCode, id: e.target.value })} />
                                </div>
                                <div className="inputGroup">
                                    <label>Password (Link ID)</label>
                                    <input required type="text" placeholder="e.g. disney25" value={editingCode.urlId}
                                        onChange={e => setEditingCode({ ...editingCode, urlId: e.target.value })} />
                                </div>
                                <div className="inputGroup" style={{ marginTop: 10 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '10px' }}>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '4px' }}>Allowed Sections</label>
                                            <span style={{ fontSize: '0.8rem', color: '#8892b0' }}>Check the sections this code can unlock.</span>
                                        </div>
                                        <div>
                                            <button type="button" onClick={() => setEditingCode({ ...editingCode, allowed_projects: [...displayRoutes, ...(editingCode.allowed_projects?.includes('/dashboard') ? ['/dashboard'] : [])] })} style={{ background: 'transparent', border: '1px solid #8892b0', color: '#8892b0', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', marginRight: '6px', transition: 'all 0.2s' }} onMouseOver={e => e.target.style.color = 'white'} onMouseOut={e => e.target.style.color = '#8892b0'}>Select All</button>
                                            <button type="button" onClick={() => setEditingCode({ ...editingCode, allowed_projects: editingCode.allowed_projects?.includes('/dashboard') ? ['/dashboard'] : [] })} style={{ background: 'transparent', border: '1px solid #8892b0', color: '#8892b0', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem', transition: 'all 0.2s' }} onMouseOver={e => e.target.style.color = 'white'} onMouseOut={e => e.target.style.color = '#8892b0'}>Clear All</button>
                                        </div>
                                    </div>
                                    <div className="routeGrid">
                                        {displayRoutes.map(route => {
                                            const checked = editingCode.allowed_projects?.includes(route);
                                            return (
                                                <label key={route} className={`routeCheck ${checked ? 'checked' : ''}`}>
                                                    <input type="checkbox" checked={checked} onChange={() => toggleRoute(route)} />
                                                    {route}
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="actions">
                                    <button type="button" className="cancel" onClick={closeCodePanel}>Clear</button>
                                    <button type="submit" className="save">Save Code</button>
                                </div>
                            </form>
                        </div>}
                    </div>
                )}

                {tab === "analytics" && (
                    <div className="analyticsTab">
                        <div className="analyticSelector" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.03)', padding: '16px', borderRadius: '8px', flexWrap: 'wrap', gap: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                    <label style={{ fontSize: '0.9rem', color: '#8892b0' }}>Fetch recent:</label>
                                    <select value={analyticsLimit} onChange={e => setAnalyticsLimit(e.target.value)} style={{ background: '#112240', color: 'white', border: '1px solid #233554', padding: '6px 12px', borderRadius: '4px', outline: 'none' }}>
                                        <option value="20">20 events</option>
                                        <option value="100">100 events</option>
                                        <option value="500">500 events</option>
                                        <option value="1000">1000 events</option>
                                        <option value="2000">2000 events</option>
                                        <option value="5000">5000 events</option>
                                    </select>
                                    <input
                                        type="text"
                                        placeholder="Specific User (Optional)"
                                        value={fetchUserName}
                                        onChange={e => setFetchUserName(e.target.value)}
                                        style={{ background: '#112240', color: 'white', border: '1px solid #233554', padding: '6px 12px', borderRadius: '4px', outline: 'none', width: '180px' }}
                                    />
                                    <button onClick={triggerAnalyticsFetch} disabled={analyticsLoading} style={{ background: analyticsLoading ? '#0a192f' : '#e62243', color: analyticsLoading ? '#8892b0' : 'white', border: '1px solid ' + (analyticsLoading ? '#233554' : '#e62243'), padding: '6px 16px', borderRadius: '4px', cursor: analyticsLoading ? 'not-allowed' : 'pointer', fontWeight: 500, transition: 'all 0.2s' }}>
                                        {analyticsLoading ? 'Fetching...' : 'Fetch Data'}
                                    </button>
                                    {analyticsCacheDate && globalAnalytics !== null && (
                                        <span style={{ fontSize: '0.85rem', color: '#64ffda', marginLeft: '8px', opacity: 0.8 }}>
                                            Last cached: {analyticsCacheDate}
                                        </span>
                                    )}
                                </div>
                                <button className="resetBtn" onClick={resetAnalytics}>
                                    <DeleteOutlineIcon style={{ fontSize: '1rem', marginRight: '4px' }} /> Reset Stats
                                </button>
                            </div>

                            {indexError && (
                                <div style={{ background: 'rgba(230, 34, 67, 0.1)', border: '1px solid #e62243', color: '#ffb3c1', padding: '16px', borderRadius: '8px', fontSize: '0.9rem', lineHeight: '1.5' }}>
                                    <strong>Index required!</strong><br />
                                    {indexError}
                                </div>
                            )}

                            {globalAnalytics !== null && !indexError && (
                                <div className="mainSelectors" style={{ marginBottom: '0' }}>
                                    <button className={analyticsField === 'login' ? 'active' : ''} onClick={() => setAnalyticsField('login')}>Logins</button>
                                    <button className={analyticsField === 'visit' ? 'active' : ''} onClick={() => setAnalyticsField('visit')}>Visitors</button>
                                    <button className={analyticsField === 'image_view' ? 'active' : ''} onClick={() => setAnalyticsField('image_view')}>Image Views</button>
                                </div>
                            )}
                        </div>

                        {analyticsLoading ? (
                            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#8892b0' }}>Loading analytics via secure network call...</div>
                        ) : globalAnalytics === null ? (
                            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#8892b0', background: 'rgba(2,12,27,0.5)', borderRadius: '8px', marginTop: '20px' }}>
                                <HistoryIcon style={{ fontSize: '3rem', marginBottom: '16px', opacity: 0.5 }} />
                                <h3>Analytics Data Not Loaded</h3>
                                <p style={{ fontSize: '0.9rem', maxWidth: '400px', margin: '0 auto', lineHeight: 1.5 }}>
                                    To completely conserve your Firestore read quota, analytics data is not fetched automatically anymore. Select your desired depth limit and click <strong>"Fetch Data"</strong> above to cautiously load stats.
                                </p>
                            </div>
                        ) : (
                            <>
                                <div className="analyticsChart">
                                    <Chart chartList={chartList} />
                                </div>
                                <div className="analyticsList">
                                    {sortedList.map((element, idx) => {
                                        const [name, value] = Object.entries(element)[0];
                                        const totalViews = Object.values(value).reduce((a, b) => a + b, 0);
                                        return (
                                            <div key={idx} className="analyticsRow">
                                                <span className="analyticsName">{name}</span>
                                                <span className="analyticsCount">{totalViews}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="activityLogSection">
                                    <h3><HistoryIcon style={{ fontSize: '1rem', verticalAlign: 'middle', marginRight: 6 }} />Activity Log ({rawLogs.length})</h3>
                                    <div className="activityLog">
                                        {rawLogs
                                            .slice(0, 200)
                                            .map((log, idx) => renderLogEntry(log, idx))}
                                        {rawLogs.length === 0 && <div className="emptyLog">No activity events found.</div>}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* ─── ADD MEDIA MODAL ─── */}
            {showModal && (
                <div className="modalOverlay" onClick={closeModal}>
                    <div className="modalBody" onClick={e => e.stopPropagation()}>
                        <div className="modalHeader">
                            <h2>{modalTarget === 'media' ? 'Add Gallery Media' : (modalTarget === 'thumbnail' ? 'Select Project Thumbnail' : 'Select Header Image')}</h2>
                            <button className="closeBtn" onClick={closeModal}><CloseIcon /></button>
                        </div>

                        <div className="modalTabs">
                            <button className={modalMode === 'paste' ? 'active' : ''} onClick={() => setModalMode('paste')}>
                                <LinkIcon style={{ fontSize: '1rem', marginRight: 4 }} /> Paste URLs
                            </button>
                            <button className={modalMode === 'cloudinary' ? 'active' : ''} onClick={() => setModalMode('cloudinary')}>
                                <FolderOpenIcon style={{ fontSize: '1rem', marginRight: 4 }} /> Browse Cloudinary
                            </button>
                            {modalTarget === 'media' && (
                                <button className={modalMode === 'video' ? 'active' : ''} onClick={() => setModalMode('video')}>
                                    <VideocamIcon style={{ fontSize: '1rem', marginRight: 4 }} /> Add Video
                                </button>
                            )}
                        </div>

                        <div className="modalContent">
                            {modalMode === 'paste' && (
                                <div className="pasteMode">
                                    <p>Paste image URLs below (one per line):</p>
                                    <textarea
                                        value={pasteText} onChange={e => setPasteText(e.target.value)}
                                        rows={8} placeholder={"https://res.cloudinary.com/.../image1.jpg\nhttps://example.com/photo.png\nhttps://..."}
                                    />
                                    <button className="confirmBtn" onClick={addPastedUrls}>
                                        {modalTarget === 'media' ? `Add ${pasteText.split('\n').filter(u => u.trim().startsWith('http')).length} Image(s)` : 'Set Image'}
                                    </button>
                                </div>
                            )}

                            {modalMode === 'cloudinary' && (
                                <div className="cloudinaryMode">
                                    <div className="folderInput">
                                        <input type="text" placeholder="Folder name, e.g. Halloweenia_xmf9k"
                                            value={cldFolder} onChange={e => setCldFolder(e.target.value)}
                                            onKeyDown={e => e.key === 'Enter' && loadCloudinaryFolder()} />
                                        <button onClick={loadCloudinaryFolder} disabled={cldLoading}>
                                            {cldLoading ? 'Loading...' : 'Load Folder'}
                                        </button>
                                    </div>

                                    {cldImages.length > 0 && (
                                        <>
                                            <div className="cldActions">
                                                {modalTarget === 'media' ? (
                                                    <>
                                                        <span>{cldImages.filter(i => i.selected && !i.broken).length} of {cldImages.filter(i => !i.broken).length} selected</span>
                                                        <button onClick={() => selectAllCld(true)}>Select All</button>
                                                        <button onClick={() => selectAllCld(false)}>Deselect All</button>
                                                    </>
                                                ) : (
                                                    <span>Select an image</span>
                                                )}
                                            </div>
                                            <div className="cldGrid">
                                                {cldImages.map((img, idx) => (
                                                    !img.broken && (
                                                        <div key={idx} className={`cldItem ${img.selected ? 'selected' : ''}`} onClick={() => toggleCldImage(idx)}>
                                                            <img src={cldThumb(img.url, 200)} alt="" onError={() => markBroken(idx)} />
                                                            <div className="checkOverlay">
                                                                {img.selected ?
                                                                    <CheckBoxIcon style={{ color: '#e62243' }} /> :
                                                                    <CheckBoxOutlineBlankIcon style={{ color: '#8892b0' }} />}
                                                            </div>
                                                        </div>
                                                    )
                                                ))}
                                            </div>
                                            <button className="confirmBtn" onClick={addCloudinarySelection}>
                                                {modalTarget === 'media' ? `Add ${cldImages.filter(i => i.selected && !i.broken).length} Image(s)` : 'Set Image'}
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}

                            {modalMode === 'video' && (
                                <div className="videoMode">
                                    <div className="inputGroup">
                                        <label>Video URL (YouTube, Vimeo, etc.)</label>
                                        <input type="text" placeholder="https://vimeo.com/123456" value={videoUrl} onChange={e => setVideoUrl(e.target.value)} />
                                    </div>
                                    <div className="inputGroup">
                                        <label>Thumbnail Image URL (shown in the gallery grid)</label>
                                        <input type="text" placeholder="https://...thumbnail.jpg" value={videoThumb} onChange={e => setVideoThumb(e.target.value)} />
                                    </div>
                                    {videoThumb && (
                                        <div className="videoThumbPreview">
                                            <img src={videoThumb} alt="Thumbnail preview" />
                                        </div>
                                    )}
                                    <button className="confirmBtn" onClick={addVideo}>Add Video</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* ─── BLOCK USERS MODAL ─── */}
            {showBlockModal && blockTargetIndex !== null && editingProject.media[blockTargetIndex] && (
                <div className="modalOverlay" onClick={closeBlockModal}>
                    <div className="modalBody blockModal" onClick={e => e.stopPropagation()}>
                        <div className="modalHeader">
                            <h2>Block Access</h2>
                            <button className="closeBtn" onClick={closeBlockModal}><CloseIcon /></button>
                        </div>

                        <div className="blockPreview">
                            <div className="previewImage">
                                {editingProject.media[blockTargetIndex].type === 'video' ? (
                                    editingProject.media[blockTargetIndex].thumbnail ? <img src={cldThumb(editingProject.media[blockTargetIndex].thumbnail, 100)} alt="" /> : <VideocamIcon />
                                ) : (
                                    <img src={cldThumb(editingProject.media[blockTargetIndex].url, 100)} alt="" />
                                )}
                            </div>
                            <div className="previewInfo">
                                <span className="label">Restricting access to:</span>
                                <span className="filename">{editingProject.media[blockTargetIndex].description || editingProject.media[blockTargetIndex].url.split('/').pop()}</span>
                            </div>
                        </div>

                        <div className="modalContent">
                            <p className="modalHint">
                                Selected users will <strong>NOT</strong> be able to see this item.
                                Only users with access to <code>{editingProject.link}</code> are shown.
                            </p>

                            <div className="blockActions">
                                <div className="summary">
                                    <strong>{(editingProject.media[blockTargetIndex].blocked_users || []).length}</strong> of {accessCodes.filter(c => c.allowed_projects?.includes(editingProject.link) || c.allowed_projects?.includes('/dashboard')).length} users blocked
                                </div>
                                <div className="btnGroup">
                                    <button type="button" onClick={() => {
                                        const allUsers = accessCodes.filter(c => c.allowed_projects?.includes(editingProject.link) || c.allowed_projects?.includes('/dashboard')).map(c => c.urlId);
                                        const m = [...editingProject.media];
                                        m[blockTargetIndex].blocked_users = allUsers;
                                        handleProjectChange("media", m);
                                    }}>Block All</button>
                                    <button type="button" onClick={() => {
                                        const m = [...editingProject.media];
                                        m[blockTargetIndex].blocked_users = [];
                                        handleProjectChange("media", m);
                                    }}>Unblock All</button>
                                </div>
                            </div>

                            <div className="userChecklist">
                                {accessCodes.filter(c => c.allowed_projects?.includes(editingProject.link) || c.allowed_projects?.includes('/dashboard')).map(c => {
                                    const isBlocked = (editingProject.media[blockTargetIndex].blocked_users || []).includes(c.urlId);
                                    return (
                                        <div key={c.urlId} className={`userCheckItem ${isBlocked ? 'blocked' : ''}`} onClick={() => toggleBlockedUser(c.urlId)}>
                                            <div className="checkState">
                                                {isBlocked ? <CheckBoxIcon className="icon blocked" /> : <CheckBoxOutlineBlankIcon className="icon" />}
                                            </div>
                                            <div className="userDetails">
                                                <span className="userName">{c.id || c.userName || "Unnamed User"}</span>
                                                <span className="userPass">Pass: {c.urlId}</span>
                                            </div>
                                            {isBlocked && <span className="blockedBadge">Blocked</span>}
                                        </div>
                                    )
                                })}
                                {accessCodes.filter(c => c.allowed_projects?.includes(editingProject.link) || c.allowed_projects?.includes('/dashboard')).length === 0 && (
                                    <div className="emptyState">
                                        <HistoryIcon style={{ fontSize: '2rem', opacity: 0.2 }} />
                                        <p>No users have access to this section yet.</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="modalFooter">
                            <button className="confirmBtn" onClick={closeBlockModal}>Done</button>
                        </div>
                    </div>
                </div>
            )}
            {/* ─── ACCESS CODE CONTEXT MENU ─── */}
            {codeContextMenu.show && (
                <div
                    ref={contextMenuRef}
                    className="codeContextMenu"
                    style={{ top: codeContextMenu.y, left: codeContextMenu.x }}
                >
                    <div className="menuItem" onClick={() => { loadCode(codeContextMenu.code); closeContextMenu(); }}>
                        <EditOutlinedIcon style={{ fontSize: '1rem' }} /> Edit
                    </div>
                    <div className="menuItem" onClick={() => { duplicateCode(codeContextMenu.code); closeContextMenu(); }}>
                        <AddCircleOutlineIcon style={{ fontSize: '1rem' }} /> Duplicate
                    </div>
                    <div className="menuDivider"></div>
                    <div className="menuItem danger" onClick={() => { delCode(null, codeContextMenu.code.urlId); closeContextMenu(); }}>
                        <DeleteOutlineIcon style={{ fontSize: '1rem' }} /> Delete
                    </div>
                </div>
            )}
        </div>
    );
};

export default Admin;
